import { BenchmarkingDashboardDetails, BenchmarkingProviderFlyoutData } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { BenchmarkingFilterData, BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { MedicareNetwork, MedicareNetworkFilterData, MedicarePrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared";
import { EffectivenessAndEfficiencyData, HospitalAndPcpCountData, ProviderOverlapData, ProvidersByNetworkData, TotalProviderCountData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { ServiceBase } from "@app/shared";
import { authenticatedDownloadAsync } from "@app/shared/AuthenticatedDownload";

/** handles communication across the wire when interacting with the network benchmarking dashboard */
export default class BenchmarkingService extends ServiceBase {
  private benchmarkingEndpoint = `${this.apiEndpoint}/benchmarking`;

  public async getBenchmarkingLinesOfBusiness(clientId: number): Promise<BenchmarkingLineOfBusinessEnum[]> {
    return await this.makeGet<BenchmarkingLineOfBusinessEnum[]>(`${clientId}/lines-of-business`, null, this.benchmarkingEndpoint);
  }

  public async getBenchmarkingPrimaryFilterData(clientId: number): Promise<BenchmarkingFilterData> {
    return await this.makeGet<BenchmarkingFilterData>(`${clientId}/primary-filters`, null, this.benchmarkingEndpoint);
  }

  public async getBenchmarkingNetworkData(primaryFilters: MedicarePrimaryFiltersPayload): Promise<MedicareNetworkFilterData> {
    if (!primaryFilters) {
      return null;
    }
    return await this.makePost<MedicareNetworkFilterData>(`get-networks`, primaryFilters, this.benchmarkingEndpoint);
  }

  public async getContractDetails(contractId: string, primaryFilters: MedicarePrimaryFiltersPayload, versionId?: number): Promise<MedicareNetwork> {
    if (!primaryFilters || !contractId) {
      return null;
    }

    return await this.makePost<MedicareNetwork>(`contract/${contractId}`, {...primaryFilters, versionId}, this.benchmarkingEndpoint);
  }

  public async getTotalProviderCountData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<TotalProviderCountData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<TotalProviderCountData[]>(`total-provider-count`, networkComparisonFiltersPayload, this.benchmarkingEndpoint);
  }

  public async getHospitalAndPcpCountData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<HospitalAndPcpCountData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<HospitalAndPcpCountData[]>(`hospital-and-pcp-count`, networkComparisonFiltersPayload, this.benchmarkingEndpoint);
  }

  public async getProvidersByNetworkData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<ProvidersByNetworkData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }
    
    return await this.makePost<ProvidersByNetworkData[]>(`providers-by-network`, networkComparisonFiltersPayload, this.benchmarkingEndpoint);
  }

  public async getEffectivenessAndEfficiencyData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<EffectivenessAndEfficiencyData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<EffectivenessAndEfficiencyData[]>(`effectiveness-and-efficiency`, networkComparisonFiltersPayload, this.benchmarkingEndpoint);
  }

  public async getProviderOverlapData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<ProviderOverlapData> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<ProviderOverlapData>(`provider-overlap`, networkComparisonFiltersPayload, this.benchmarkingEndpoint);
  }

  public async getDashboardDetailsData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<BenchmarkingDashboardDetails[]> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<BenchmarkingDashboardDetails[]>(`dashboard-details`, networkComparisonFiltersPayload, this.benchmarkingEndpoint);
  }

  public async getProviderFlyoutData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<BenchmarkingProviderFlyoutData> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<BenchmarkingProviderFlyoutData>(`provider-details`, networkComparisonFiltersPayload, this.benchmarkingEndpoint);
  }

  public async getDataCoverageCsv(fileName?: string, clientId?: number): Promise<void> {
      let urlSearchParams = new URLSearchParams();

      if (!!clientId) {
          const query = {
              clientId: clientId.toString()
          };
          urlSearchParams = new URLSearchParams(query)
      }

      if (!!fileName && fileName.split('.').pop().toLowerCase() !== `csv`) {
          fileName += `.csv`;
      }

      return await authenticatedDownloadAsync(`${this.benchmarkingEndpoint}/data-coverage/report/csv`, null, fileName, urlSearchParams);
  }
}
